/**
 * <hidden-ticket-rules> — keeps ticket type and chosen date consistent.
 *
 * Weekend and Weekday are not independent choices: they are properties of the date.
 * A single booking happens on one day, so a basket can't hold both. Student is a
 * weekday concession (dev, 2026-09-28: no Student on weekends or holidays), so it is
 * listed under the weekday names; the any-date list exists for stores that do sell
 * a concession on every date.
 *
 * Which days are "the weekend" is a pricing decision, not a calendar fact — this
 * store sells Friday at the weekend rate — so it comes from the `weekend-days`
 * setting (getDay() numbers) rather than being hard-coded to Sat/Sun.
 *
 * Some days are split by time of day instead (client, 2026-10-23: Friday is Off-Peak
 * 11:00–16:30 and Peak from 17:00). On a `split-days` day the date alone can't say
 * which rate applies, so the chosen time slot does: whatever ticket types Evey
 * offers for that slot. Public holidays stay at the weekend rate all day.
 *
 * It also holds the basket inside the event's seat count. Evey reports one `available`
 * figure shared by every ticket type, so the limit is on their sum, not per row.
 *
 * And it gates Add to Cart on a per-slot capacity that INCLUDES what is already in
 * the cart (client spec, 2026-09-28): at most `slot-capacity` tickets per date+time,
 * where on a weekday the Weekday and Student tickets share the pool and on a weekend
 * or public holiday only the Weekend ticket counts. Evey's `available` does not see
 * tickets sitting in carts, so this is the check that stops the 16th ticket being
 * added across two separate adds. See checkCartCapacity().
 *
 * The constraint runs both ways, whichever the customer does first:
 *   pick a date  -> the ticket type that doesn't match that day is disabled
 *   add tickets  -> the days that don't match that type are disabled in the calendar
 * Clearing quantities or the date releases the constraint.
 *
 * IMPORTANT: this is an affordance, not enforcement. It can be bypassed with
 * devtools, so Evey's own per-date ticket-type restriction is still required before
 * launch — otherwise a weekday ticket can be bought for a weekend date.
 *
 * Promo tickets (Asana 1220060590739612, Deepavali 6–9 Nov 2026) are variants that
 * only some events offer. Rows named in `promo-names` stay hidden until the chosen
 * slot offers them, and a slot that offers one shows only its own ticket types —
 * the regular Peak/Off-Peak/Student rows don't display beside the promo. Slots
 * without a promo ticket behave exactly as before (unoffered rows greyed out), and
 * an empty `promo-names` changes nothing.
 *
 * Evey exposes no JS API, so this drives its DOM directly (selectors verified
 * against the rendered storefront 2026-09-14):
 *   form.evey__multi-variant-selector
 *   div.evey__variant-container > .evey__variant-name + input.quantity__input
 *   #evey-schedule-calendar-modal td.day-of-month[data-date="YYYY-MM-DD"]
 * Evey re-renders the calendar on month navigation, so rules are re-applied from a
 * MutationObserver rather than bound once.
 */

const BLOCKED = 'hidden-rule-blocked';
const HIDDEN = 'hidden-rule-hidden';

class HiddenTicketRules extends HTMLElement {
  connectedCallback() {
    this.weekendNames = this.names('weekendNames', 'weekend');
    this.weekdayNames = this.names('weekdayNames', 'weekday');
    this.anyNames = this.names('anyNames', 'student');
    this.promoNames = this.names('promoNames', '');
    this.weekendDays = this.days('weekendDays', '0,5,6');
    this.splitDays = this.splitDaySet('splitDays');
    this.publicHolidays = this.dates('publicHolidays');
    this.slotCapacity = this.integer('slotCapacity', 15);
    this.capacityError =
      this.dataset.capacityError ||
      'Only [remaining] of [cap] tickets left for this date and time. Please reduce the quantity.';

    /** @type {string | null} 'YYYY-MM-DD' of the chosen day */
    this.selectedDate = null;

    // Capture phase: we must be able to stop Evey's own handler on a blocked day.
    this.onClick = this.onClick.bind(this);
    document.addEventListener('click', this.onClick, true);

    this.onChange = () => {
      // A rejected add stays on screen until the customer changes something.
      this.clearCapacityError();
      this.apply();
    };
    document.addEventListener('change', this.onChange);
    document.addEventListener('input', this.onChange);

    // Evey renders asynchronously and re-renders the calendar per month.
    this.observer = new MutationObserver(() => this.apply());
    this.observer.observe(document.body, { childList: true, subtree: true });

    this.apply();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onClick, true);
    document.removeEventListener('change', this.onChange);
    document.removeEventListener('input', this.onChange);
    this.observer?.disconnect();
  }

  /**
   * Lower-cased name fragments from a data attribute. An attribute that is present
   * but EMPTY means "no tickets in this category" and must not fall back — the
   * any-date list is empty on this store since Student became weekday-only
   * (dev, 2026-09-28).
   * @returns {string[]}
   */
  names(key, fallback) {
    return (this.dataset[key] ?? fallback)
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
  }

  /**
   * Which days count as the weekend, as getDay() numbers (0 Sun … 6 Sat).
   * Configurable because it is a pricing rule, not a fact: this store sells Friday
   * at the weekend rate (dev, 2026-09-17), and other HIDDEN sites may not.
   * @returns {Set<number>}
   */
  days(key, fallback) {
    const parsed = (this.dataset[key] || fallback)
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => Number.isInteger(n) && n >= 0 && n <= 6);
    // An empty or malformed setting would silently make every date a weekday and
    // block Weekend tickets outright, so fall back rather than trust it.
    return new Set(parsed.length ? parsed : [0, 5, 6]);
  }

  /**
   * Days whose rate is decided by the time slot, as getDay() numbers. Unlike
   * weekend-days an empty setting is trusted: it just means no day is split.
   * @returns {Set<number>}
   */
  splitDaySet(key) {
    return new Set(
      (this.dataset[key] || '')
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => Number.isInteger(n) && n >= 0 && n <= 6)
    );
  }

  /**
   * Public holidays as 'YYYY-MM-DD' strings from a comma/newline separated setting.
   * Anything that doesn't look like a date is dropped rather than trusted.
   * @returns {Set<string>}
   */
  dates(key) {
    return new Set(
      (this.dataset[key] || '')
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s))
    );
  }

  /** @returns {number} a positive integer setting, or the fallback */
  integer(key, fallback) {
    const n = parseInt(this.dataset[key] || '', 10);
    return Number.isInteger(n) && n > 0 ? n : fallback;
  }

  /** @returns {HTMLElement[]} */
  get rows() {
    return Array.from(document.querySelectorAll('.evey__variant-container'));
  }

  /** @returns {'weekend'|'weekday'|'any'|null} */
  categoryOf(row) {
    return this.categoryOfName(row.querySelector('.evey__variant-name')?.textContent || '');
  }

  /**
   * Ticket category from a ticket's name — the row's label on the PDP, or the
   * variant title of a cart line, which are the same string.
   * @param {string} name
   * @returns {'weekend'|'weekday'|'any'|null}
   */
  categoryOfName(name) {
    const lower = (name || '').toLowerCase();
    if (!lower) return null;

    // The LONGEST matching fragment wins, whichever list it sits in. The lists are
    // plain substrings, and since the variants were renamed Peak / Off-Peak (dev,
    // 2026-09-29) they nest: "off-peak" contains "peak", so first-list-wins filed
    // every Off-Peak row under Peak. Exact-length ties keep the old order.
    let best = null;
    let bestLength = 0;
    const consider = (fragments, category) => {
      for (const fragment of fragments) {
        if (fragment.length > bestLength && lower.includes(fragment)) {
          best = category;
          bestLength = fragment.length;
        }
      }
    };
    consider(this.anyNames, 'any');
    consider(this.weekendNames, 'weekend');
    consider(this.weekdayNames, 'weekday');
    return best;
  }

  /** A promo ticket row — see `promo-names` in the header comment. */
  isPromo(row) {
    const lower = (row.querySelector('.evey__variant-name')?.textContent || '').toLowerCase();
    return this.promoNames.some((fragment) => lower.includes(fragment));
  }

  /**
   * A weekend for pricing purposes: a configured weekend day OR a public holiday
   * (client spec, 2026-09-28 — holidays sell at the weekend rate).
   * @param {string} iso 'YYYY-MM-DD' — parsed as parts so the local timezone can't shift the day
   */
  isWeekendDate(iso) {
    if (this.publicHolidays.has(iso)) return true;
    const [y, m, d] = iso.split('-').map(Number);
    return this.weekendDays.has(new Date(y, m - 1, d).getDay());
  }

  /**
   * A day whose rate depends on the time slot. A public holiday is never split —
   * it sells at the weekend rate all day.
   * @param {string} iso 'YYYY-MM-DD'
   */
  isSplitDate(iso) {
    if (this.publicHolidays.has(iso)) return false;
    const [y, m, d] = iso.split('-').map(Number);
    return this.splitDays.has(new Date(y, m - 1, d).getDay());
  }

  /**
   * The rate of the chosen time slot, read from the ticket types Evey offers for it.
   * @param {Set<string> | null} offered - from offeredVariantIds()
   * @returns {'weekend'|'weekday'|null} null when no slot is chosen, or it offers both
   */
  slotCategory(offered) {
    if (!offered) return null;
    const cats = new Set(
      this.rows.filter((row) => offered.has(row.getAttribute('data-variant-id'))).map((row) => this.categoryOf(row))
    );
    if (cats.has('weekend') && !cats.has('weekday')) return 'weekend';
    if (cats.has('weekday') && !cats.has('weekend')) return 'weekday';
    return null;
  }

  /**
   * Which rate applies on a date: by the date itself, or on a split day by the
   * chosen slot (null until one is chosen).
   * @param {string} iso 'YYYY-MM-DD'
   * @param {Set<string> | null} offered
   * @returns {'weekend'|'weekday'|null}
   */
  dayCategory(iso, offered) {
    if (this.isSplitDate(iso)) return this.slotCategory(offered);
    return this.isWeekendDate(iso) ? 'weekend' : 'weekday';
  }

  static qtyOf(row) {
    const input = row.querySelector('input.quantity__input');
    return input ? parseInt(input.value, 10) || 0 : 0;
  }

  /** Which category the current basket demands, or null when unconstrained. */
  demandedCategory() {
    for (const row of this.rows) {
      const cat = this.categoryOf(row);
      if ((cat === 'weekend' || cat === 'weekday') && HiddenTicketRules.qtyOf(row) > 0) return cat;
    }
    return null;
  }

  onClick(event) {
    const cell = event.target.closest?.('td.day-of-month[data-date]');
    if (cell) {
      if (cell.classList.contains(BLOCKED)) {
        // Blocked day: stop Evey from acting on it.
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      // Evey synthesises a click on today's cell while initialising the calendar.
      // Only a real user gesture counts as choosing a date — otherwise the page
      // loads already constrained to today's day-of-week, which silently blocked
      // the opposite ticket type and stopped the calendar rule from ever running.
      if (!event.isTrusted) return;
      // Days with no availability aren't a selection either.
      if (!cell.classList.contains('has-events')) return;
      this.selectedDate = cell.dataset.date;
      this.clearCapacityError();
      // Let Evey handle it first, then reflect the new constraint.
      setTimeout(() => this.apply(), 0);
      return;
    }

    // Quantity steppers mutate the input after their own handler runs.
    if (event.target.closest?.('.evey__variant_quantity')) setTimeout(() => this.apply(), 0);
  }

  /**
   * Variant ids the currently selected Evey event actually offers, or null when no
   * event is selected / Evey hasn't exposed one.
   * @returns {Set<string> | null}
   */
  static offeredVariantIds() {
    const ev = window.currentScheduleEvent;
    if (!ev || !Array.isArray(ev.ticket_types)) return null;
    return new Set(ev.ticket_types.map((t) => String(t.variant_id)));
  }

  /**
   * Evey ships a per-row "Maximum reached" line but only reveals it for its own
   * per-variant limit, which never fires here: the limit that bites is the shared
   * pool across all three types, which Evey doesn't track. So we reveal it.
   *
   * Ownership is marked on the element, and we only claim a line Evey is currently
   * hiding. If Evey has its own reason to show one, it is left alone and never
   * hidden by us.
   *
   * @param {HTMLElement} row
   * @param {boolean} show
   */
  static setAtCapacityMessage(row, show) {
    const el = row.querySelector('.evey__variant-limit');
    if (!el) return;
    const ours = el.dataset.hiddenCapMessage === 'true';

    if (show) {
      if (!ours) {
        // Already visible => Evey is saying something of its own. Don't take it over.
        if (getComputedStyle(el).display !== 'none') return;
        el.dataset.hiddenCapMessage = 'true';
      }
      // Write only on change: style writes are childList/attribute mutations our own
      // observer picks up, and writing unconditionally re-enters apply() forever.
      if (el.style.display !== 'block') el.style.display = 'block';
    } else if (ours) {
      delete el.dataset.hiddenCapMessage;
      if (el.style.display !== 'none') el.style.display = 'none';
    }
  }

  /**
   * Evey's product-level "Only N tickets left" sits directly under the rows. Once the
   * pool is spent the rows already say "Maximum reached", and leaving a line that
   * offers tickets underneath one that refuses them reads as a contradiction — so it
   * is hidden for as long as that holds.
   *
   * The inline display Evey had is stored when we hide it and put back verbatim when
   * we stop, so Evey keeps control of the line outside the at-capacity window. A line
   * Evey is already hiding is never claimed.
   *
   * @param {boolean} hide
   */
  static setProductLimitHidden(hide) {
    const el = document.querySelector('.evey__product-message .evey__product-limit');
    if (!el) return;
    const previous = el.dataset.hiddenCapPrevDisplay;

    if (hide) {
      if (previous !== undefined) return; // already ours
      if (getComputedStyle(el).display === 'none') return; // Evey hides it anyway
      el.dataset.hiddenCapPrevDisplay = el.style.display || '';
      el.style.display = 'none';
    } else if (previous !== undefined) {
      delete el.dataset.hiddenCapPrevDisplay;
      el.style.display = previous;
    }
  }

  /**
   * Seats left for the chosen timeslot.
   *
   * Evey reports the SAME `available` on every ticket type because they all draw
   * from one pool per event — "available": 15 on Weekend, Weekday and Student means
   * fifteen seats in total, not fifteen of each. So this is a cap on the SUM of all
   * quantities, and the smallest reported figure is the safe reading of it.
   *
   * `currentScheduleEvent` is per selected date and is authoritative once a date is
   * chosen; `window.ticketTypes` is the product-level default Evey prints inline and
   * is the only figure available before then.
   *
   * Only ticket types the customer can buy right now count. The product-level list
   * includes types with no events yet (the Festive promo: `available` null for the
   * single, 0 for the group tickets), and one of those must not read as "sold out"
   * for every row — that showed Maximum reached across the page (2026-11-03).
   *
   * @param {Set<string>} buyable - variant ids of rows that aren't blocked or hidden
   * @returns {number | null} null when Evey hasn't reported availability yet, in
   *   which case nothing is capped — guessing a limit would block real purchases.
   */
  static capacity(buyable) {
    const types = window.currentScheduleEvent?.ticket_types ?? window.ticketTypes;
    if (!Array.isArray(types) || !types.length) return null;
    const counts = types
      .filter((t) => buyable.has(String(t.variant_id)))
      .filter((t) => t.available != null)
      .map((t) => Number(t.available))
      .filter((n) => Number.isInteger(n) && n >= 0);
    return counts.length ? Math.min(...counts) : null;
  }

  /**
   * Holds the total across every buyable row at or under `cap`.
   *
   * Prevention first: the + button is disabled and `max` is set once the pool is
   * spent, so the customer can't go over in the normal flow. The clamp behind it
   * catches the cases a disabled button can't — typing or pasting a number, and
   * switching to a date with fewer seats than are already in the basket.
   *
   * Blocked rows are excluded, matching updateTotal(): they can't be bought, so they
   * can't consume seats.
   *
   * Rows are filled in DOM order, so when the pool has to be taken back it comes off
   * the lower rows first and the customer's earlier choices survive.
   *
   * @param {number | null} cap
   * @returns {HTMLInputElement[]} inputs whose value was changed
   */
  enforceCapacity(cap) {
    const touched = [];
    const all = this.rows;
    // Blocked rows are excluded, matching updateTotal(): they can't be bought, so
    // they can't consume seats. Their controls belong to the blocking pass, and a
    // row that becomes blocked must drop any message we put on it.
    const blocked = all.filter((row) => row.classList.contains(BLOCKED));
    for (const row of blocked) HiddenTicketRules.setAtCapacityMessage(row, false);

    const cells = all
      .filter((row) => !row.classList.contains(BLOCKED))
      .map((row) => ({
        row,
        input: row.querySelector('input[name="items[][quantity]"]'),
        plus: row.querySelector('.quantity__plus'),
      }))
      .filter((c) => c.input);

    if (cap === null) {
      for (const { row, input } of cells) {
        input.removeAttribute('max');
        HiddenTicketRules.setAtCapacityMessage(row, false);
      }
      HiddenTicketRules.setProductLimitHidden(false);
      return touched;
    }

    // Pass 1 — bring the sum under the cap. Seats are handed out in DOM order, so
    // when they have to be taken back it comes off the lower rows and the choices
    // made first survive.
    let remaining = cap;
    for (const { input } of cells) {
      const qty = parseInt(input.value, 10) || 0;
      const allowed = Math.min(qty, remaining);
      if (qty > allowed) {
        input.value = String(allowed);
        touched.push(input);
      }
      remaining -= allowed;
    }

    // Pass 2 — headroom is what the pool has left overall, not what happened to be
    // left when a given row was reached. Using the running figure would leave the
    // top row's + enabled at capacity, and raising it would silently take seats back
    // off the rows below.
    const atCapacity = remaining <= 0;
    for (const { row, input, plus } of cells) {
      const qty = parseInt(input.value, 10) || 0;
      input.max = String(qty + remaining);
      if (plus) plus.disabled = atCapacity;
      // Shown on every buyable row, because the pool being spent stops all of them
      // being increased, not just the ones the customer happened to fill.
      HiddenTicketRules.setAtCapacityMessage(row, atCapacity);
    }

    // Only suppress the count while there is a row visibly refusing more tickets.
    HiddenTicketRules.setProductLimitHidden(atCapacity && cells.length > 0);

    return touched;
  }

  /**
   * Which ticket categories draw on one slot's capacity for a given day type.
   *
   * Weekday: Weekday and Student tickets share the pool.
   * Weekend / public holiday: only the Weekend ticket counts — verbatim from the
   * client spec (2026-09-28), which lists the Weekend ticket alone for those days.
   *
   * @param {'weekend'|'weekday'} dayType
   * @returns {Set<string>}
   */
  static pooledCategories(dayType) {
    return dayType === 'weekend' ? new Set(['weekend']) : new Set(['weekday', 'any']);
  }

  /**
   * The capacity arithmetic, kept pure so it can be tested without a DOM.
   *
   * @param {object} args
   * @param {string} args.eventId - Evey's `_event_id` for the chosen slot
   * @param {'weekend'|'weekday'} args.dayType - what kind of day that slot falls on
   * @param {{ eventId: string, category: string | null, quantity: number }[]} args.cartLines
   * @param {{ category: string | null, quantity: number }[]} args.additions - rows being added
   * @param {number} args.cap
   * @returns {{ ok: boolean, inCart: number, adding: number, remaining: number, cap: number }}
   */
  static slotUsage({ eventId, dayType, cartLines, additions, cap }) {
    const pooled = HiddenTicketRules.pooledCategories(dayType);
    const counts = (category) => pooled.has(category);

    const inCart = cartLines
      .filter((line) => line.eventId && line.eventId === eventId && counts(line.category))
      .reduce((sum, line) => sum + (line.quantity || 0), 0);

    const adding = additions
      .filter((row) => counts(row.category))
      .reduce((sum, row) => sum + (row.quantity || 0), 0);

    return {
      ok: inCart + adding <= cap,
      inCart,
      adding,
      remaining: Math.max(0, cap - inCart),
      cap,
    };
  }

  /**
   * Evey's `_event_id` is "start|scheduleItem|end" with the start as a unix time.
   * The calendar's own selection is preferred for the day type (it is exactly the
   * date the customer clicked, in the venue's calendar); the timestamp is the
   * fallback when the selection isn't known, e.g. the page was restored with a date
   * already set.
   * @param {string} eventId
   * @returns {string | null} 'YYYY-MM-DD'
   */
  slotDate(eventId) {
    if (this.selectedDate) return this.selectedDate;
    const start = parseInt(String(eventId).split('|')[0], 10);
    if (!Number.isFinite(start)) return null;
    const d = new Date(start * 1000);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  /**
   * Gate for Add to Cart: would this add push the chosen slot past its capacity,
   * counting what the cart already holds for that slot?
   *
   * Called by hidden-cart-drawer.js before it posts the form. The cart is passed in
   * (fresh from /cart.js) rather than fetched here so the caller controls the
   * request. Rows the weekend/weekday rule has blocked are ignored — they are
   * disabled and won't be submitted.
   *
   * @param {HTMLFormElement} form - Evey's multi-ticket form
   * @param {{ items: { variant_title: string, quantity: number, properties: object }[] }} cart
   * @returns {{ ok: boolean, message: string, remaining: number, cap: number }}
   */
  checkCartCapacity(form, cart) {
    const eventId = form.querySelector('input[name="properties[_event_id]"]')?.value || '';
    const allow = { ok: true, message: '', remaining: this.slotCapacity, cap: this.slotCapacity };
    // No slot chosen: Evey's own validation refuses the add; nothing to gate here.
    if (!eventId) return allow;

    const date = this.slotDate(eventId);
    // On a split day the slot decides the pool; an Off-Peak Friday slot must count
    // Off-Peak tickets, not the Peak ones the date alone would imply.
    const dayType =
      (date && this.dayCategory(date, HiddenTicketRules.offeredVariantIds())) ||
      (date && this.isWeekendDate(date) ? 'weekend' : 'weekday');

    const additions = this.rows
      .filter((row) => !row.classList.contains(BLOCKED))
      .map((row) => ({ category: this.categoryOf(row), quantity: HiddenTicketRules.qtyOf(row) }));

    const cartLines = (cart?.items || []).map((item) => ({
      eventId: item.properties?._event_id || '',
      category: this.categoryOfName(item.variant_title),
      quantity: item.quantity,
    }));

    const usage = HiddenTicketRules.slotUsage({
      eventId,
      dayType,
      cartLines,
      additions,
      cap: this.slotCapacity,
    });

    if (usage.ok) return allow;

    // Square-bracket placeholders: braces would read as Liquid inside the block's
    // schema JSON, where the default lives.
    const message = this.capacityError
      .replace(/\[\s*remaining\s*\]/g, String(usage.remaining))
      .replace(/\[\s*cap\s*\]/g, String(usage.cap))
      .replace(/\[\s*in_cart\s*\]/g, String(usage.inCart));
    return { ok: false, message, remaining: usage.remaining, cap: usage.cap };
  }

  /**
   * The rejection, shown directly above Add to Cart where Evey puts its own
   * product-level messages. One element, reused; cleared on the next change.
   * @param {string} text
   */
  showCapacityError(text) {
    const form = document.querySelector('form.evey__multi-variant-selector');
    if (!form) return;
    let el = form.querySelector('.hidden-capacity-error');
    if (!el) {
      el = document.createElement('p');
      el.className = 'hidden-capacity-error';
      el.setAttribute('role', 'alert');
      const anchor = form.querySelector('.evey__product-message') || form.querySelector('button.product-form__submit');
      if (anchor) anchor.before(el);
      else form.appendChild(el);
    }
    if (el.textContent !== text) el.textContent = text;
  }

  clearCapacityError() {
    document.querySelector('form.evey__multi-variant-selector .hidden-capacity-error')?.remove();
  }

  /**
   * WORKAROUND for a crash in Evey's own inline validate().
   *
   * It walks `.evey__variant_quantity .quantity__input`, looks each row's variant up
   * in the selected event's ticket_types, and reads `ticketType.available` WITHOUT a
   * null check. When an event doesn't offer every variant on the product — which is
   * exactly what Evey's own per-date ticket restriction produces — the lookup returns
   * null, validate() throws, and `evey__isvalid` is never set, so Add to Cart stays
   * disabled forever.
   *
   * Dropping the `quantity__input` class on rows the event doesn't offer takes them
   * out of that query, so validate() only ever sees rows it can resolve. The class is
   * restored the moment the row is offered again — nothing is detached or reordered.
   *
   * Remove this once Evey null-guards that lookup.
   * @param {HTMLElement} row
   * @param {boolean} hide
   */
  static setHiddenFromEvey(row, hide) {
    const input = row.querySelector('input[name="items[][quantity]"]');
    if (!input) return;
    if (hide) {
      if (input.classList.contains('quantity__input')) {
        input.classList.remove('quantity__input');
        input.classList.add('hidden-evey-skipped');
      }
    } else if (input.classList.contains('hidden-evey-skipped')) {
      input.classList.remove('hidden-evey-skipped');
      input.classList.add('quantity__input');
    }
  }

  /** Nudge Evey to re-run its validation after we change which rows it can see. */
  static revalidate() {
    const input = document.querySelector('.evey__variant_quantity .quantity__input');
    if (!input) return;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Mirrors the frame's "ADD TO CART   $61.00" running total onto Evey's button.
   * Evey renders only the label, so the total is appended as our own span and kept
   * in sync from the visible ticket rows — rows the event doesn't offer, or that the
   * weekend/weekday rule blocks, are excluded because they can't be purchased.
   *
   * Prices are read from Evey's rendered text because the DOM exposes no numeric
   * attribute for them; the currency symbol is reused from that same text so this
   * doesn't hard-code a currency.
   */
  updateTotal() {
    const button = document.querySelector('.evey__multi-variant-selector button.product-form__submit');
    if (!button) return;

    let total = 0;
    let symbol = '';
    let sawPrice = false;

    for (const row of this.rows) {
      if (row.classList.contains(BLOCKED)) continue;
      const text = row.querySelector('.evey__variant-price')?.textContent || '';
      const match = text.match(/([^\d\s]*)\s*([\d.,]+)/);
      if (!match) continue;
      sawPrice = true;
      if (!symbol) symbol = match[1] || '';
      const unit = parseFloat(match[2].replace(/,/g, ''));
      if (Number.isNaN(unit)) continue;
      total += unit * HiddenTicketRules.qtyOf(row);
    }

    if (!sawPrice) return;

    const next = total > 0 ? `${symbol}${total.toFixed(2)}` : '';

    let el = button.querySelector('.hidden-atc-total');
    if (!el) {
      el = document.createElement('span');
      el.className = 'hidden-atc-total';
      button.appendChild(el);
    }
    // Write ONLY on change. Assigning textContent replaces child nodes, which is a
    // childList mutation our own MutationObserver picks up — writing unconditionally
    // re-entered apply() forever and pegged the main thread.
    if (el.textContent !== next) el.textContent = next;
  }

  apply() {
    if (this.applying) return; // our own DOM writes re-enter through the observer
    this.applying = true;

    let visibilityChanged = false;
    let clamped = [];
    const reset = [];
    try {
      const demanded = this.demandedCategory();
      const offered = HiddenTicketRules.offeredVariantIds();

      // Date chosen -> constrain ticket types to that day (or, on a split day, to
      // the chosen slot — null until one is picked, so neither type is blocked).
      const dateCategory = this.selectedDate ? this.dayCategory(this.selectedDate, offered) : null;

      // A slot that offers a promo ticket shows only what it offers.
      const promoSlot =
        !!offered && this.rows.some((row) => this.isPromo(row) && offered.has(row.getAttribute('data-variant-id')));

      for (const row of this.rows) {
        const cat = this.categoryOf(row);
        // 'any' (Student) is never blocked; unrecognised rows are left alone.
        let blocked = false;
        if (cat === 'weekend' || cat === 'weekday') {
          if (dateCategory && cat !== dateCategory) blocked = true;
          // Weekend and Weekday are mutually exclusive within one booking: once one
          // is chosen the other is unbuyable, otherwise both constraints together
          // would block every day in the calendar with nothing explaining why.
          if (!dateCategory && demanded && cat !== demanded) blocked = true;
        }

        // A row the selected event doesn't offer is unbuyable regardless of the
        // weekend/weekday rule, and must also be hidden from Evey's validate().
        const unoffered = offered ? !offered.has(row.getAttribute('data-variant-id')) : false;
        if (unoffered) blocked = true;

        // A promo row with no slot chosen yet has no slot to be offered by.
        const hidden = this.isPromo(row) ? !offered || unoffered : promoSlot && unoffered;
        if (hidden) blocked = true;
        row.classList.toggle(HIDDEN, hidden);

        const wasSkipped = !!row.querySelector('input.hidden-evey-skipped');
        if (wasSkipped !== unoffered) visibilityChanged = true;
        HiddenTicketRules.setHiddenFromEvey(row, unoffered);

        // A blocked row can't be bought, so its quantity goes back to 0 rather than
        // sitting greyed out with a number the customer won't get.
        const qtyInput = row.querySelector('input[name="items[][quantity]"]');
        if (blocked && qtyInput && (parseInt(qtyInput.value, 10) || 0) !== 0) {
          qtyInput.value = '0';
          reset.push(qtyInput);
        }

        row.classList.toggle(BLOCKED, blocked);
        row.setAttribute('aria-disabled', String(blocked));
        // Disable EVERY input in a blocked row, hidden ones included. Evey submits
        // parallel arrays (items[][id], items[][quantity]); disabling only the visible
        // quantity would drop one quantity while leaving its id in place, shifting the
        // arrays so the wrong variant receives the wrong count. Disabling the whole row
        // removes it from the submission cleanly and keeps the arrays aligned.
        row.querySelectorAll('button, input').forEach((el) => {
          el.disabled = blocked;
        });
      }

      // Every ticket type draws on one pool of seats, so the cap is on their sum.
      // Runs after the blocking pass because a blocked row must not hold seats.
      const buyable = new Set(
        this.rows.filter((row) => !row.classList.contains(BLOCKED)).map((row) => row.getAttribute('data-variant-id'))
      );
      clamped = this.enforceCapacity(HiddenTicketRules.capacity(buyable));

      // Ticket type chosen -> constrain the calendar to matching days.
      //
      // This holds whether or not a date is already selected. Previously picking a
      // date released it, so a basket with Weekend tickets would suddenly offer
      // weekday dates again — the basket still demands a weekend, so the calendar
      // must keep saying so. Student never contributes, being an any-date concession.
      const required = demanded;
      document.querySelectorAll('td.day-of-month[data-date]').forEach((cell) => {
        let blocked = false;
        // A split day sells both rates, so it suits either basket.
        if (required && !this.isSplitDate(cell.dataset.date)) {
          const weekend = this.isWeekendDate(cell.dataset.date);
          blocked = required === 'weekend' ? !weekend : weekend;
        }
        cell.classList.toggle(BLOCKED, blocked);
        cell.setAttribute('aria-disabled', String(blocked));
      });
    } finally {
      this.applying = false;
    }

    this.updateTotal();

    // Done outside the guard: these re-enter through Evey's listeners, and must not
    // be swallowed by our own re-entrancy flag.
    //
    // A clamped input has to tell Evey, or its own line totals and validation keep
    // the number the customer typed while the field shows the number they'll get.
    // Safe from looping: the clamp only fires while qty exceeds the cap, and the
    // write that triggers this has already brought it back under.
    // Same for a blocked row zeroed above.
    for (const input of [...reset, ...clamped]) input.dispatchEvent(new Event('change', { bubbles: true }));
    if (visibilityChanged) HiddenTicketRules.revalidate();
  }
}

if (!customElements.get('hidden-ticket-rules')) {
  customElements.define('hidden-ticket-rules', HiddenTicketRules);
}
